import { datadogRum } from '@datadog/browser-rum';
import Config from 'configs';
import pjson from '../../package.json';

type TrackingConsent = 'granted' | 'not-granted';

type DidomiConsentStatus = {
  purposes?: {
    global?: {
      enabled?: boolean;
    };
    consent?: {
      enabled?: string[];
    };
  };
  vendors?: {
    global?: {
      enabled?: boolean;
    };
  };
};

type DidomiApi = {
  on: (eventName: 'consent.changed', callback: () => void) => void;
  getCurrentUserStatus?: () => DidomiConsentStatus;
  getUserStatus?: () => Promise<DidomiConsentStatus> | DidomiConsentStatus;
};

type WindowWithDidomi = Window & {
  Didomi?: DidomiApi;
  didomiOnReady?: Array<(didomi: DidomiApi) => void>;
};

const PACKAGE_VERSION_RUNTIME_MARKER = '__PACKAGE_JSON_RUNTIME__';
const DEFAULT_SESSION_SAMPLE_RATE = 20;
// Note: "measur" (stem) is used instead of "measure" so it also matches the real BNC Didomi
// purpose id "measuring-improving-experience" ("measuring", not "measure").
const ANALYTICS_PURPOSE_PATTERN = /(analytic|measur|performance|stat)/i;
// Didomi custom (non-IAB) vendor ids follow the `<slug>-<randomSuffix>` convention (e.g.
// "exponentia-tT9zj8XW"), not a bare slug. Match "datadog" as a whole segment, optionally
// followed by "-<suffix>", instead of requiring an exact "datadog" key — the exact vendor id
// assigned by the Didomi console for this BNC tenant could not be confirmed statically and may
// include such a suffix.
const DATADOG_VENDOR_ID_PATTERN = /^datadog(-.+)?$/i;
const EMAIL_QUERY_VALUE_PATTERN = /[^/?#&=]+@[^/?#&=]+\.[^/?#&=]+/i;
const EMAIL_REDACTION_PATTERN = /[^/?#&=]+@[^/?#&=]+\.[^/?#&=]+/gi;
const SENSITIVE_QUERY_KEY_PATTERN = /(email|mail|e-?mail|sin|social(?:-?insurance)?(?:-?number)?|bncid)/i;
const SIN_VALUE_PATTERN = /(^|[^0-9])\d{3}[- ]?\d{3}[- ]?\d{3}([^0-9]|$)/;

let rumInitialized = false;
let didomiConsentListenerRegistered = false;
let didomiReadyHandlerRegistered = false;

const getRumRuntimeConfig = () => {
  const config = Config.params;
  const rumConfig = config?.RUM;
  const configuredSampleRate = rumConfig?.sessionSampleRate;

  return {
    enabled: config?.FEATURE_TOGGLE?.RUM_ENABLED === true,
    applicationId: rumConfig?.applicationId?.trim() || '',
    clientToken: rumConfig?.clientToken?.trim() || '',
    site: rumConfig?.site?.trim() || 'datadoghq.com',
    service: rumConfig?.service?.trim() || 'bao-front-end',
    env: rumConfig?.env?.trim() || '',
    version: pjson.version || rumConfig?.version || PACKAGE_VERSION_RUNTIME_MARKER,
    // Sampling is environment-driven so production can stay well below 100% for both
    // Datadog cost and PII exposure. Fall back to the conservative value when unset.
    sessionSampleRate:
      typeof configuredSampleRate === 'number' && configuredSampleRate >= 0 && configuredSampleRate <= 100
        ? configuredSampleRate
        : DEFAULT_SESSION_SAMPLE_RATE,
  };
};

const redactSensitiveUrl = (rawUrl?: string): string | undefined => {
  if (!rawUrl) {
    return rawUrl;
  }

  try {
    const parsedUrl = new URL(rawUrl, window.location.origin);
    const redactedParams = new URLSearchParams(parsedUrl.search);

    Array.from(redactedParams.entries()).forEach(([key, value]) => {
      if (
        SENSITIVE_QUERY_KEY_PATTERN.test(key) ||
        EMAIL_QUERY_VALUE_PATTERN.test(value) ||
        SIN_VALUE_PATTERN.test(value)
      ) {
        redactedParams.delete(key);
      }
    });

    const redactedSearch = redactedParams.toString();
    parsedUrl.search = redactedSearch ? `?${redactedSearch}` : '';

    return parsedUrl.toString().replace(EMAIL_REDACTION_PATTERN, '[REDACTED_EMAIL]');
  } catch (error) {
    return rawUrl.replace(EMAIL_REDACTION_PATTERN, '[REDACTED_EMAIL]');
  }
};

const beforeSend = (event: any): boolean => {
  if (event?.view?.url) {
    event.view.url = redactSensitiveUrl(event.view.url);
  }

  if (event?.view?.referrer) {
    event.view.referrer = redactSensitiveUrl(event.view.referrer);
  }

  if (event?.resource?.url) {
    event.resource.url = redactSensitiveUrl(event.resource.url);
  }

  if (event?.action?.target?.name) {
    event.action.target.name = String(event.action.target.name).replace(EMAIL_REDACTION_PATTERN, '[REDACTED_EMAIL]');
  }

  // Defensive guardrail only: a manual DOM/PII audit of automatically collected action target names
  // is still required before enabling RUM in production.
  return true;
};

const hasDidomiAnalyticsConsent = (status?: DidomiConsentStatus): boolean => {
  if (!status) {
    return false;
  }

  // Legacy/simplified shapes (kept for compatibility with earlier assumptions and unit tests).
  const enabledPurposes = status?.purposes?.consent?.enabled ?? [];
  if (
    status?.purposes?.global?.enabled === true ||
    status?.vendors?.global?.enabled === true ||
    enabledPurposes.some((purposeId) => ANALYTICS_PURPOSE_PATTERN.test(purposeId))
  ) {
    return true;
  }

  // Real BNC Didomi CMP shape: `vendors` is a flat map keyed by vendor id, each with its own
  // `enabled` flag. Datadog is registered as a custom (non-IAB) vendor whose id starts with
  // "datadog" (optionally suffixed, per Didomi's `<slug>-<randomSuffix>` convention for custom
  // vendors) — trust that signal directly since it is the most precise and intentional consent
  // grant for this integration.
  const vendorsMap = status?.vendors as Record<string, { enabled?: boolean }> | undefined;
  if (vendorsMap && typeof vendorsMap === 'object') {
    const datadogVendorGranted = Object.entries(vendorsMap).some(
      ([vendorId, vendor]) => vendor?.enabled === true && DATADOG_VENDOR_ID_PATTERN.test(vendorId),
    );
    if (datadogVendorGranted) {
      return true;
    }
  }

  // Real BNC Didomi CMP shape: `purposes` is also a flat map keyed by purpose id (e.g.
  // "measuring-improving-experience"), each with its own `enabled` flag, rather than the
  // `purposes.consent.enabled` array assumed above. Match any enabled purpose whose id looks
  // like an analytics/measurement purpose.
  const purposesMap = status?.purposes as Record<string, { id?: string; enabled?: boolean }> | undefined;
  if (purposesMap && typeof purposesMap === 'object') {
    return Object.entries(purposesMap).some(
      ([purposeId, purpose]) => purpose?.enabled === true && ANALYTICS_PURPOSE_PATTERN.test(purpose?.id ?? purposeId),
    );
  }

  return false;
};

const updateRumTrackingConsent = async (didomi: DidomiApi): Promise<void> => {
  try {
    const status =
      didomi.getCurrentUserStatus?.() ??
      (didomi.getUserStatus ? await Promise.resolve(didomi.getUserStatus()) : undefined);
    const consent: TrackingConsent = hasDidomiAnalyticsConsent(status) ? 'granted' : 'not-granted';
    datadogRum.setTrackingConsent(consent);
  } catch (error) {
    console.warn('[datadog-rum] Failed to resolve Didomi consent status, defaulting to not-granted.', error);
    datadogRum.setTrackingConsent('not-granted');
  }
};

const registerDidomiConsentListener = (didomi: DidomiApi): void => {
  if (didomiConsentListenerRegistered) {
    return;
  }

  void updateRumTrackingConsent(didomi);
  didomi.on('consent.changed', () => {
    void updateRumTrackingConsent(didomi);
  });
  didomiConsentListenerRegistered = true;
};

export const initDatadogRum = (): void => {
  try {
    if (rumInitialized) {
      return;
    }

    const {
      enabled,
      applicationId,
      clientToken,
      site,
      service,
      env,
      version,
      sessionSampleRate,
    } = getRumRuntimeConfig();

    if (!enabled) {
      console.info('[datadog-rum] RUM init skipped because FEATURE_TOGGLE.RUM_ENABLED is false.');
      return;
    }

    if (!applicationId || !clientToken) {
      console.info('[datadog-rum] RUM init skipped because applicationId/clientToken are not configured yet.');
      return;
    }

    datadogRum.init({
      applicationId,
      clientToken,
      site,
      service,
      env,
      version,
      trackingConsent: 'not-granted',
      // Root cause (confirmed live in prod, 2026-09-08): the BAO app is actually served/backed in
      // production under `accounts.nbdb.ca` and `comptes.bncd.ca` (National Bank Direct Brokerage),
      // NOT under a `*.bnc.ca` subdomain. The previous single-pattern allow-list only matched
      // `*.bnc.ca`, so the RUM SDK never injected distributed-tracing headers on real prod backend
      // calls — causing span_count: 0 on the RUM↔APM correlation dashboard regardless of consent.
      allowedTracingUrls: [/https:\/\/.*\.bnc\.ca/, /https:\/\/.*\.nbdb\.ca/, /https:\/\/.*\.bncd\.ca/],
      trackResources: true,
      trackUserInteractions: true,
      trackViewsManually: true,
      sessionSampleRate,
      beforeSend,
    });

    rumInitialized = true;
  } catch (error) {
    console.error('[datadog-rum] Failed to initialize Datadog RUM. Continuing without RUM.', error);
  }
};

export const wireDatadogRumConsent = (): void => {
  try {
    const typedWindow = window as WindowWithDidomi;

    if (typedWindow.Didomi) {
      registerDidomiConsentListener(typedWindow.Didomi);
      return;
    }

    if (didomiReadyHandlerRegistered) {
      console.info('[datadog-rum] Waiting for Didomi to become available.');
      return;
    }

    typedWindow.didomiOnReady = typedWindow.didomiOnReady ?? [];
    typedWindow.didomiOnReady.push((didomi) => {
      registerDidomiConsentListener(didomi);
    });
    didomiReadyHandlerRegistered = true;
    console.info('[datadog-rum] Waiting for Didomi to become available.');
  } catch (error) {
    console.error('[datadog-rum] Failed to wire Didomi consent for Datadog RUM.', error);
  }
};

export const identifyRumUser = (bncId: string): void => {
  if (!rumInitialized || !bncId?.trim()) {
    return;
  }

  datadogRum.setUser({ id: bncId });
};

export const clearRumUser = (): void => {
  if (!rumInitialized) {
    return;
  }

  datadogRum.clearUser();
};

export const setRumRequestCode = (requestCode: string): void => {
  if (!rumInitialized || !requestCode?.trim()) {
    return;
  }

  datadogRum.setGlobalContextProperty('request_code', requestCode);
};

export const clearRumRequestCode = (): void => {
  if (!rumInitialized) {
    return;
  }

  datadogRum.removeGlobalContextProperty('request_code');
};

export const isDatadogRumInitialized = (): boolean => rumInitialized;

export const resetDatadogRumState = (): void => {
  rumInitialized = false;
  didomiConsentListenerRegistered = false;
  didomiReadyHandlerRegistered = false;
};
